import { Box, Flex, Text } from '@chakra-ui/react'
import { ContentNode, ContentNodeUtils, Format } from '@helo/cms'
import { Assert, pipe } from '@helo/utils'
import type { ReactNode } from 'react'

import { MilestoneBarcodeIcon } from '../../components/Icons/MilestoneBarcodeIcon'
import { MilestoneCalendarIcon } from '../../components/Icons/MilestoneCalendarIcon'
import { MilestoneLocationPinIcon } from '../../components/Icons/MilestoneLocationPinIcon'
import { MilestoneMegaphoneIcon } from '../../components/Icons/MilestoneMegaphoneIcon'
import { Skeleton, SkeletonCircle } from '../../components/SkeletonLoader'
import { TypeRenderer } from '../../datatypes/TypeRenderer'
import { toWallClockDateString } from '../../utils/dates'
import { withUnicodeRouteArrow } from '../../utils/routeLabelDisplay'
import {
  MILESTONE_SKELETON,
  MILESTONE_TEXT_WRAP,
  MILESTONE_TIMELINE,
  MILESTONE_TYPE,
  MILESTONE_TYPOGRAPHY,
  MILESTONE_VARIANTS,
  type MilestoneDisplayOptions,
  MilestoneLayout,
  type MilestoneTypeName,
  type MilestoneVariant,
  type TimelineLineSegment,
} from './milestone.constants'

const getTimelineLineSegment = (
  index: number,
  total: number,
): TimelineLineSegment => {
  if (total <= 1) return 'only'
  if (index === 0) return 'first'
  if (index === total - 1) return 'last'
  return 'middle'
}

const getTimelineLinePosition = (
  segment: TimelineLineSegment,
): { top: string; bottom: string } | null => {
  switch (segment) {
    case 'only':
      return null
    case 'first':
      return {
        top: MILESTONE_TIMELINE.dotCenter,
        bottom: '-1px',
      }
    case 'middle':
      return { top: '-1px', bottom: '-1px' }
    case 'last':
      return { top: '-1px', bottom: MILESTONE_TIMELINE.dotCenter }
    default:
      return null
  }
}

const findShipmentDocumentId = (milestoneContentNode: ContentNode) =>
  ContentNodeUtils.findContentNode([
    ['key', 'ShipmentDocument'],
    ['key', 'shipmentDocumentId'],
  ])(milestoneContentNode) ??
  ContentNodeUtils.findContentNode([
    ['key', 'TransportUnitShipmentDocument'],
    ['key', 'ShipmentDocument'],
    ['key', 'shipmentDocumentId'],
  ])(milestoneContentNode)

const resolveDisplayOptions = ({
  variant,
  hideShipmentDocumentId,
  hideIssuer,
}: {
  variant?: MilestoneVariant
  hideShipmentDocumentId?: boolean
  hideIssuer?: boolean
}): MilestoneDisplayOptions => {
  const resolvedVariant =
    variant ??
    (hideShipmentDocumentId && hideIssuer ? 'cluster' : 'detailDrawer')
  const preset = MILESTONE_VARIANTS[resolvedVariant]
  return {
    hideShipmentDocumentId:
      hideShipmentDocumentId ?? preset.hideShipmentDocumentId,
    hideIssuer: hideIssuer ?? preset.hideIssuer,
  }
}

const hasRightColumn = (display: MilestoneDisplayOptions) =>
  !display.hideShipmentDocumentId || !display.hideIssuer

const MilestoneHeloIcon = () => (
  <svg
    width={MILESTONE_TIMELINE.pillIconSize}
    height={MILESTONE_TIMELINE.pillIconSize}
    viewBox="0 0 12 11"
    preserveAspectRatio="xMidYMid meet"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Group">
      <path
        id="Vector"
        d="M2.35986 7.07963C2.36646 7.02026 2.37086 6.96089 2.37526 6.90042C2.36756 6.95979 2.36316 7.01916 2.35986 7.07963Z"
        fill="url(#paint0_radial_4126_4688)"
      />
      <path
        id="Vector_2"
        d="M6.36086 4.78394C6.29049 4.87519 6.22782 4.96755 6.17505 5.0632C6.24102 4.96865 6.30259 4.87519 6.36086 4.78394Z"
        fill="url(#paint1_radial_4126_4688)"
      />
      <path
        id="Vector_3"
        d="M5.45482 9.01029C4.08817 7.64475 5.55377 6.19674 6.17497 5.06209C6.22775 4.96643 6.29042 4.87408 6.36078 4.78282C8.11224 1.99896 5.86382 0.736761 4.20032 0.729065C3.92435 0.729065 3.65058 0.752154 3.38121 0.796133C3.55603 0.793934 5.12278 0.799431 4.98205 1.80875C4.83362 2.87633 2.52363 4.56622 2.3741 6.9004C2.3697 6.96087 2.3653 7.02025 2.35871 7.07962C2.28394 8.60129 3.67147 10.5869 6.3223 10.2285C7.1645 10.1142 7.94952 9.84479 8.65318 9.46657C8.53334 9.52374 6.77199 10.3297 5.45372 9.01029H5.45482Z"
        fill="url(#paint2_radial_4126_4688)"
      />
      <path
        id="Vector_4"
        d="M9.57678 3.58544C7.81322 4.86413 6.17501 5.06313 6.17501 5.06313C4.79077 5.19617 3.72539 5.19397 2.90628 5.10491C-0.334966 4.75198 0.300529 3.02471 0.397283 2.79932C0.326917 2.93565 0.265346 3.07638 0.213671 3.22481C-0.845121 6.30773 2.37523 6.90035 2.37523 6.90035C3.31968 7.16312 4.18167 7.26317 4.96559 7.25438C7.95616 7.22139 9.82526 5.59527 10.8676 5.26104C12.0748 4.87402 11.7691 5.93061 11.7098 6.10983C11.757 5.98449 11.7999 5.85915 11.8362 5.73381C12.4321 3.64811 11.3392 2.30675 9.57678 3.58544Z"
        fill="url(#paint3_radial_4126_4688)"
      />
      <path
        id="Vector_5"
        d="M1.0371 4.57172C1.34385 3.63607 2.23772 2.65095 4.63018 2.58388C4.81818 2.29692 4.95122 2.03854 4.98311 1.80985C5.12384 0.800534 3.55709 0.795037 3.38227 0.796137C2.11018 1.00504 0.960136 1.7065 0.397206 2.79938C0.32684 2.9643 -0.0282893 3.92523 1.0371 4.57063V4.57172Z"
        fill="url(#paint4_radial_4126_4688)"
      />
      <path
        id="Vector_6"
        d="M8.65428 9.46654C10.1144 8.68042 11.2149 7.42482 11.7097 6.10875C11.7691 5.92954 12.0747 4.87294 10.8675 5.25996C10.6718 5.32263 10.4475 5.43038 10.1924 5.56451C9.17983 7.07738 7.41627 8.25052 5.16016 8.64963C5.23822 8.76947 5.33497 8.88931 5.45372 9.00916C6.77198 10.3274 8.53444 9.52151 8.65318 9.46544L8.65428 9.46654Z"
        fill="url(#paint5_radial_4126_4688)"
      />
    </g>
    <defs>
      <radialGradient
        id="paint0_radial_4126_4688"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(2.36756 6.98948) scale(0.0637701)"
      >
        <stop offset="0.26" stopColor="#F89F1E" />
        <stop offset="0.6" stopColor="#EB1D63" />
        <stop offset="0.68" stopColor="#D61F65" />
        <stop offset="0.86" stopColor="#A0266B" />
        <stop offset="1" stopColor="#722D71" />
      </radialGradient>
      <radialGradient
        id="paint1_radial_4126_4688"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(6.2674 4.92357) scale(0.118743)"
      >
        <stop offset="0.26" stopColor="#F89F1E" />
        <stop offset="0.6" stopColor="#EB1D63" />
        <stop offset="0.68" stopColor="#D61F65" />
        <stop offset="0.86" stopColor="#A0266B" />
        <stop offset="1" stopColor="#722D71" />
      </radialGradient>
      <radialGradient
        id="paint2_radial_4126_4688"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(9.42831 0.0957693) scale(11.4972 11.4972)"
      >
        <stop offset="0.26" stopColor="#F89F1E" />
        <stop offset="0.6" stopColor="#EB1D63" />
        <stop offset="0.68" stopColor="#D61F65" />
        <stop offset="0.86" stopColor="#A0266B" />
        <stop offset="1" stopColor="#722D71" />
      </radialGradient>
      <radialGradient
        id="paint3_radial_4126_4688"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(9.92421 1.18859) scale(10.2647)"
      >
        <stop stopColor="#81C99B" />
        <stop offset="0.04" stopColor="#65C19B" />
        <stop offset="0.13" stopColor="#39B69B" />
        <stop offset="0.2" stopColor="#1AAD9B" />
        <stop offset="0.26" stopColor="#06A89B" />
        <stop offset="0.3" stopColor="#00A79C" />
        <stop offset="0.51" stopColor="#2192A9" />
        <stop offset="0.74" stopColor="#4D78BC" />
        <stop offset="0.8" stopColor="#4C73B7" />
        <stop offset="0.85" stopColor="#4C66AA" />
        <stop offset="0.91" stopColor="#4B5094" />
        <stop offset="0.98" stopColor="#4A3275" />
        <stop offset="1" stopColor="#4A2568" />
      </radialGradient>
      <radialGradient
        id="paint4_radial_4126_4688"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(0.288359 5.6591) scale(7.47861 7.47861)"
      >
        <stop offset="0.26" stopColor="#F89F1E" />
        <stop offset="0.6" stopColor="#EB1D63" />
        <stop offset="0.68" stopColor="#D61F65" />
        <stop offset="0.86" stopColor="#A0266B" />
        <stop offset="1" stopColor="#722D71" />
      </radialGradient>
      <radialGradient
        id="paint5_radial_4126_4688"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(6.23654 10.8992) scale(7.34667)"
      >
        <stop offset="0.26" stopColor="#F89F1E" />
        <stop offset="0.6" stopColor="#EB1D63" />
        <stop offset="0.68" stopColor="#D61F65" />
        <stop offset="0.86" stopColor="#A0266B" />
        <stop offset="1" stopColor="#722D71" />
      </radialGradient>
    </defs>
  </svg>
)

interface MilestonePillProps {
  label: string | null | ReactNode
  icon: ReactNode
  type: MilestoneTypeName
  showPill?: boolean
  withHorizontalPadding?: boolean
  allCaps?: boolean
}

const MilestonePill = ({
  label = '-',
  icon,
  showPill = false,
  withHorizontalPadding = true,
  type,
  allCaps = false,
}: MilestonePillProps) => {
  const pillPadding = showPill
    ? MILESTONE_TIMELINE.pillPadding
    : withHorizontalPadding
      ? MILESTONE_TIMELINE.pillPadding
      : '4px 0'

  return (
    <Flex flex="1 1 0" {...MilestoneLayout.flexRow()}>
      <Flex
        alignItems="center"
        rounded="full"
        padding={pillPadding}
        background={showPill ? 'presets.neutral.10' : 'none'}
        gap={MILESTONE_TIMELINE.pillInnerGap}
        {...MilestoneLayout.flexItem()}
      >
        <Flex
          w={MILESTONE_TIMELINE.pillIconSize}
          h={MILESTONE_TIMELINE.pillIconSize}
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
        >
          {icon}
        </Flex>
        <Text
          {...MILESTONE_TYPOGRAPHY.pill}
          color={`presets.${MilestoneLayout.pillTextColor(type)}`}
          textTransform={allCaps ? 'uppercase' : 'none'}
          {...MILESTONE_TEXT_WRAP}
        >
          {label ? label : '-'}
        </Text>
      </Flex>
    </Flex>
  )
}

interface MilestoneTimelineGutterProps {
  lineSegment: TimelineLineSegment
  children: ReactNode
}

const MilestoneTimelineGutter = ({
  lineSegment,
  children,
}: MilestoneTimelineGutterProps) => {
  const linePosition = getTimelineLinePosition(lineSegment)

  return (
    <Box
      w={MILESTONE_TIMELINE.gutterWidth}
      position="relative"
      flexShrink={0}
      alignSelf="stretch"
    >
      {linePosition ? (
        <Box
          position="absolute"
          left="calc(50% - 1px)"
          top={linePosition.top}
          bottom={linePosition.bottom}
          w={MILESTONE_TIMELINE.lineWidth}
          bg="presets.blue.40"
          boxShadow="none"
          zIndex={0}
        />
      ) : null}
      <Flex
        h={MILESTONE_TIMELINE.gutterRowHeight}
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        {children}
      </Flex>
    </Box>
  )
}

interface SkeletonPillProps {
  maxW: string
}

const SkeletonPill = ({ maxW }: SkeletonPillProps) => (
  <Flex flex="1 1 0" {...MilestoneLayout.flexRow()}>
    <Flex
      alignItems="center"
      padding={MILESTONE_TIMELINE.pillPadding}
      gap={MILESTONE_TIMELINE.pillInnerGap}
      {...MilestoneLayout.flexItem()}
    >
      <SkeletonCircle
        isLoading
        size={MILESTONE_TIMELINE.pillIconSize}
        flexShrink={0}
      />
      <Skeleton isLoading h={MILESTONE_SKELETON.titleHeight} w={maxW} />
    </Flex>
  </Flex>
)

interface MilestoneSkeletonRowProps {
  lineSegment: TimelineLineSegment
  display: MilestoneDisplayOptions
}

const MilestoneSkeletonRow = ({
  lineSegment,
  display,
}: MilestoneSkeletonRowProps) => (
  <Box {...MilestoneLayout.rowContainer()}>
    <MilestoneTimelineGutter lineSegment={lineSegment}>
      <SkeletonCircle
        isLoading
        size={MILESTONE_TIMELINE.dotSize}
        position="relative"
        zIndex={1}
      />
    </MilestoneTimelineGutter>
    <Flex {...MilestoneLayout.rowContent()}>
      <Flex {...MilestoneLayout.titleRow()}>
        <Skeleton
          isLoading
          h={MILESTONE_SKELETON.titleHeight}
          w={MILESTONE_SKELETON.titleWidth}
          flex="none"
        />
      </Flex>
      <Flex {...MilestoneLayout.pillColumns()}>
        <Flex {...MilestoneLayout.pillColumn()}>
          <SkeletonPill maxW={MILESTONE_SKELETON.pillWidth} />
          <SkeletonPill maxW={MILESTONE_SKELETON.pillWidth} />
        </Flex>
        {hasRightColumn(display) ? (
          <Flex {...MilestoneLayout.pillColumn()}>
            <SkeletonPill maxW={MILESTONE_SKELETON.pillWidth} />
            <SkeletonPill maxW={MILESTONE_SKELETON.pillWidth} />
          </Flex>
        ) : null}
      </Flex>
    </Flex>
  </Box>
)

interface MilestoneListSkeletonProps {
  display: MilestoneDisplayOptions
}

export const MilestoneListSkeleton = ({
  display,
}: MilestoneListSkeletonProps) => (
  <Flex {...MilestoneLayout.columnList()}>
    {MILESTONE_SKELETON.lineSegments.map((segment, index) => (
      <MilestoneSkeletonRow
        key={segment + String(index)}
        lineSegment={segment}
        display={display}
      />
    ))}
  </Flex>
)

interface MilestoneRowProps {
  milestoneContentNode: ContentNode
  index: number
  total: number
  parentShipmentDocumentId?: ContentNode
  display: MilestoneDisplayOptions
}

export const MilestoneRow = ({
  milestoneContentNode,
  index,
  total,
  parentShipmentDocumentId,
  display,
}: MilestoneRowProps) => {
  const type = ContentNodeUtils.findContentNode([['key', 'milestoneTypeName']])(
    milestoneContentNode,
  )?.data as MilestoneTypeName

  const milestoneName =
    ContentNodeUtils.findContentNode([['key', 'partnerMilestoneName']])(
      milestoneContentNode,
    ) ??
    ContentNodeUtils.findContentNode([['key', 'heloMilestoneName']])(
      milestoneContentNode,
    )

  const dateTime = ContentNodeUtils.findContentNode([['key', 'date']])(
    milestoneContentNode,
  )

  const placeContentNode = ContentNodeUtils.findContentNode([['key', 'Place']])(
    milestoneContentNode,
  )

  const Place = placeContentNode
    ? pipe(
        placeContentNode,
        Assert.orThrow(ContentNodeUtils.isKind('FIELD')),
        ContentNodeUtils.getOfType(),
        ContentNodeUtils.setState([
          {
            state: {
              size: 'small',
              _tooltip: {
                label: 'Service Provider Location Name:',
                asValue: ContentNodeUtils.findContentNode([
                  ['key', 'displayLabel'],
                ])(placeContentNode)?.data as string | undefined,
              },
            },
          },
        ] as any),
      )
    : null

  const placeDisplayValue = ContentNodeUtils.findContentNode([
    ['key', 'placeDisplayValue'],
  ])(milestoneContentNode)

  const placeDisplayValueFromPlace = ContentNodeUtils.findContentNode([
    ['key', 'Place'],
    ['key', 'displayValue'],
  ])(milestoneContentNode)

  const shipmentDocumentId = findShipmentDocumentId(milestoneContentNode)

  const issuer = ContentNodeUtils.findContentNode([['key', 'issuer']])(
    milestoneContentNode,
  )

  const placeLabel: ReactNode = placeDisplayValueFromPlace?.data ? (
    withUnicodeRouteArrow(Format.text({})(placeDisplayValueFromPlace.data))
  ) : Place ? (
    <TypeRenderer contentNode={Place as ContentNode} />
  ) : (
    withUnicodeRouteArrow(Format.text({})(placeDisplayValue?.data))
  )

  const lineSegment = getTimelineLineSegment(index, total)

  return (
    <Box {...MilestoneLayout.rowContainer()}>
      <MilestoneTimelineGutter lineSegment={lineSegment}>
        <Box
          h={MILESTONE_TIMELINE.dotSize}
          w={MILESTONE_TIMELINE.dotSize}
          rounded="full"
          flexShrink={0}
          position="relative"
          zIndex={1}
          {...(type === MILESTONE_TYPE.prediction
            ? {
                borderWidth: '2px',
                borderStyle: 'solid',
                borderColor: 'presets.blue.50',
                bg: 'presets.neutral.0',
                boxShadow: 'none',
              }
            : {
                border: 'none',
                bg: 'presets.blue.50',
                boxShadow: 'none',
              })}
        />
      </MilestoneTimelineGutter>
      <Flex {...MilestoneLayout.rowContent()}>
        <Flex {...MilestoneLayout.titleRow()}>
          <Text
            fontSize={MILESTONE_TYPOGRAPHY.title.fontSize}
            fontWeight={MilestoneLayout.titleFontWeight(index)}
            color={MilestoneLayout.titleTextColor(type)}
            lineHeight="normal"
            {...MILESTONE_TEXT_WRAP}
          >
            {Format.text({})(milestoneName?.data)}
          </Text>
        </Flex>
        <Flex {...MilestoneLayout.pillColumns()}>
          <Flex {...MilestoneLayout.pillColumn()}>
            <MilestonePill
              type={type}
              withHorizontalPadding
              label={Format.date()(toWallClockDateString(dateTime?.data))}
              icon={
                <MilestoneCalendarIcon
                  boxSize={MILESTONE_TIMELINE.pillIconSize}
                  color="presets.neutral.70"
                />
              }
            />
            <MilestonePill
              label={placeLabel}
              withHorizontalPadding
              type={type}
              allCaps
              icon={
                <MilestoneLocationPinIcon
                  boxSize={MILESTONE_TIMELINE.pillIconSize}
                  color="presets.neutral.70"
                />
              }
            />
          </Flex>
          {hasRightColumn(display) ? (
            <Flex {...MilestoneLayout.pillColumn()}>
              {!display.hideShipmentDocumentId && (
                <MilestonePill
                  type={type}
                  withHorizontalPadding
                  label={Format.id({})(
                    shipmentDocumentId?.data ?? parentShipmentDocumentId?.data,
                  )}
                  icon={
                    <MilestoneBarcodeIcon
                      boxSize={MILESTONE_TIMELINE.pillIconSize}
                      color="presets.neutral.70"
                    />
                  }
                />
              )}
              {!display.hideIssuer && (
                <MilestonePill
                  type={type}
                  withHorizontalPadding
                  label={Format.text({})(issuer?.data)}
                  icon={
                    String(issuer?.data ?? '').toLowerCase() === 'helo' ? (
                      <MilestoneHeloIcon />
                    ) : (
                      <MilestoneMegaphoneIcon
                        boxSize={MILESTONE_TIMELINE.pillIconSize}
                        color="presets.neutral.70"
                      />
                    )
                  }
                  showPill
                />
              )}
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </Box>
  )
}

export const MilestoneParts = {
  resolveDisplayOptions,
}
